import cn from "classnames";
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import tomorrowThingsLogo from "@src/assets/svg/tt-logo.svg";
import { VerticalSeparator } from "@components/atoms/vertical-separator/vertical-separator";
import { Icon } from "@components/atoms/icon/icon";
import { LogoutIconButtonWrapperWithTooltip } from "@components/molecules/logout-icon-button-wrapper-with-tooltip/logout-icon-button-wrapper-with-tooltip";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { ThirdSplitContainer } from "@components/templates/layouts/third-split-container/third-split-container";
import { DropAreaDeactivationOverlayContainer } from "@components/templates/layouts/drop-area-deactivation-overlay-container/drop-area-deactivation-overlay-container";
import styles from "./navigation-wrapper.module.scss";
import { HelpIconWithPopover } from "@components/organisms/help-icon-with-popover/help-icon-with-popover";
import { RenderOnlyForDevelopers } from "@components/molecules/render-only-for-developers/render-only-for-developers";
import { RenderUnreleasedFeatures } from "@src/components/molecules/render-unreleased-features/render-unreleased-features";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { useAuth } from "react-oidc-context";
import { UsersIconButton } from "@src/components/atoms/users-icon-button/users-icon-button";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { DropDownLanguage } from "@src/components/atoms/dropdown-language-selector/dropdown-language";
import { useI18N } from "@src/language/language-context";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useUsersIconButtonHook } from "@src/components/atoms/users-icon-button/use-users-icon-button-hook";
import { RenderIfUserHasMultiplePermissions } from "@src/components/molecules/render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { FlexColumnContainer } from "../flex-column-container/flex-column-container";
import { performLogout } from "@src/auth/utils/logout";
import { ChatIconButton } from "@src/components/atoms/chat-icon-button/chat-icon-button";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { SupportTicketButton } from "@src/components/atoms/support-ticket-button/support-ticket-button";
import { NotificationsIconButton } from "@src/components/atoms/notifications-icon-button/notifications-icon-button";
import { InfoIconWithPopover } from "@src/components/organisms/info-icon-popover/info-icon-popover";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface Feature {
  id: string;
  url: string;
  name: string;
  icon: string;
  enabled: boolean;
  dev: boolean;
  unreleased: boolean;
  tooltip?: string;
}

interface WrappedElement {
  element: ReactElement;
  enabled: boolean;
  id: string;
}

function isWrappedElement(
  featureOrElement: Feature | WrappedElement,
): featureOrElement is WrappedElement {
  return (featureOrElement as WrappedElement).element !== undefined;
}

const cssClassName = "navbar";
const cssClassNameOuter = "navbar-outer";

const FeatureLink = ({
  featureOrElement,
}: {
  featureOrElement: Feature | WrappedElement;
}) => {
  const location = useLocation();

  if (!featureOrElement.enabled) {
    return null;
  }

  if (isWrappedElement(featureOrElement)) {
    return featureOrElement.element;
  }

  const handleRoutingAnomaly = () => {
    return (
      (url === "/" && location.pathname.includes(Route.NODE_MANAGER_PAGE)) ||
      (url === "/" && location.pathname.includes(Route.MACHINES))
    );
  };

  const { id, url, name, icon, dev, enabled, unreleased, tooltip } =
    featureOrElement;

  const ListElement = () => {
    return (
      <li className={styles[`${cssClassName}__nav-item`]} key={id}>
        <ToolTip content={tooltip ?? ""} position="bottom">
          <NavLink
            to={url}
            onClick={(e) => {
              if (url !== "/nodemanager") {
                e.preventDefault();
                getNodeManager().performChangesCheckWhenNavigatingAwayFromNodeManager(
                  url,
                );
              }
            }}
            className={({ isActive }) =>
              cn(
                styles[`${cssClassName}__nav-link`],
                (isActive || handleRoutingAnomaly()) &&
                  styles[`${cssClassName}__nav-link--selected`],
              )
            }
          >
            <Icon name={icon} width={40} height={40} />
            <span className={styles[`${cssClassName}__nav-text`]}>{name}</span>
          </NavLink>
        </ToolTip>
      </li>
    );
  };

  return dev && enabled ? (
    <RenderOnlyForDevelopers>
      <ListElement />
    </RenderOnlyForDevelopers>
  ) : unreleased && enabled ? (
    <RenderUnreleasedFeatures>
      <ListElement />
    </RenderUnreleasedFeatures>
  ) : (
    <ListElement />
  );
};

const FACTORY_DASHBOARD_PERMISSIONS = ["view-dashboards", "view-timeseries"];

export const NavigationWrapper = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const i18n = useI18N();
  const dispatch = useDispatch();
  const showFactoryDashboard =
    useFeatureFlag("show-factory-dashboard", false) === true;
  const isAdminPanelEnabled = useFeatureFlag("admin-panel", false);

  const texts = {
    gateways: i18n("modules.navigation.gateways"),
    dashboards: i18n("modules.navigation.dashboards"),
    licences: i18n("modules.navigation.licences"),
    developerPanel: i18n("modules.navigation.developerPanel"),
    usersIconButton: i18n("modules.navigation.usersIconButton"),
    ttLogoAltText: i18n("modules.navigation.ttLogoAltText"),
    productName: i18n("modules.navigation.productName"),
    developerButtonToolTip: i18n("modules.navigation.developerButtonToolTip"),
    machines: i18n("modules.navigation.machines"),
    tickets: i18n("modules.navigation.tickets"),
    homepage: i18n("modules.navigation.homepage"),
    chatAI: i18n("modules.navigation.chatAI"),
    support: i18n("modules.navigation.support"),
    notifications: i18n("modules.navigation.notifications"),
    factoryDashboard: i18n("modules.navigation.factoryDashboard"),
    dashboardBuilder: i18n("modules.navigation.dashboardBuilder"),
    dashboard: i18n("modules.navigation.dashboard"),
    adminPanel: i18n("modules.navigation.adminPanel"),
  };

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const namespace = useSelector(
    (state: RootState) => state.yourSlice.authContext?.customerNamespace,
  );

  // ticketing features get deactivated for namespace a6f962a7-77f7-46f2-9c72-59580255fdf8 (controlled via the "fakuma-hide-ticketing" feature flag)
  const isTicketingFeatureEnabled = useFeatureFlag("fakuma-hide-ticketing");

  const [userFullName, setUserFullName] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | undefined | null>(undefined);

  const handleLogOutClick = async () => {
    await performLogout(auth, dispatch).catch((error) => {
      console.error("Logout failed", error);
    });
    navigateTo(Route.LOGOUT);
  };

  const features: (Feature | WrappedElement)[] = [
    {
      id: "gateways",
      url: "/gateways",
      name: "Gateways",
      icon: "gateways_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.gateways,
    },
    {
      id: "configuration-manager",
      url: "/configuration-manager",
      name: "Configuration Manager",
      icon: "tt_hypercube",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.licences,
    },
    {
      id: "machines",
      url: "/machines",
      name: "Machines",
      icon: "machines_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.machines,
    },
    {
      id: "tickets",
      url: "/tickets",
      name: "Tickets",
      icon: "ticket_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.tickets,
    },
    {
      id: "factorydashboard",
      url: Route.FACTORY_DASHBOARD,
      name: "Factory Dashboard",
      icon: "factory_dashboard_icon",
      enabled: showFactoryDashboard,
      dev: false,
      unreleased: false,
      tooltip: texts.factoryDashboard,
    },
    {
      id: "dashboard-builder",
      url: Route.DASHBOARD_BUILDER,
      name: "Dashboard-builder",
      icon: "dashboard_builder_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.dashboardBuilder,
    },
    {
      id: "dashboard",
      url: Route.DASHBOARD,
      name: "Dashboard",
      icon: "dashboard_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.dashboard,
    },
    {
      id: "admin-panel",
      url: Route.ADMIN_PANEL,
      name: "Admin Panel",
      icon: "admin_panel_icon",
      enabled: true,
      dev: false,
      unreleased: false,
      tooltip: texts.adminPanel,
    },
    {
      element: (
        <VerticalSeparator
          style={{ marginTop: "18px", marginLeft: "8px", marginRight: "8px" }}
        />
      ),
      id: "search-separator",
      enabled: false,
    },
    /*  {
      id: "search",
      url: "/",
      name: "Search",
      icon: "tt_search",
      enabled: false,
      dev: true,
      unreleased: false,
    },
    {
      id: "settings",
      url: "/",
      name: "Settings",
      icon: "tt_settings",
      enabled: false,
      dev: true,
      unreleased: false,
    }, */
  ];

  useEffect(() => {
    if (authContext) {
      setUserFullName(authContext.fullName);
    }
  }, [authContext]);

  useEffect(() => {
    if (!namespace) return;

    const cdnUrl = `https://d1fmfcun1uqqxl.cloudfront.net/ns/${namespace}/asset/logo.svg`;

    const img = new Image();
    img.onload = () => {
      setLogoUrl(cdnUrl);
    };
    img.onerror = () => {
      setLogoUrl(null);
    };
    img.src = cdnUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [namespace]);

  return (
    <>
      <DropAreaDeactivationOverlayContainer>
        <div className={styles[cssClassNameOuter]}>
          <nav className={styles[cssClassName]}>
            <ThirdSplitContainer alignCenter={true} flexGrow={true}>
              <div className={styles[`${cssClassName}__logo-wrapper`]}>
                {/* <ToolTip position="bottom" content={texts.homepage}>
                  <NavLink
                    to="/"
                    className={styles[`${cssClassName}__logo-button`]}
                    aria-label={texts.ttLogoAltText}
                    onClick={(e) => {
                      handleNavigateToHomepage(e);
                    }}
                  >
                    <img src={tomorrowThingsLogo} alt={texts.ttLogoAltText} />
                  </NavLink>
                </ToolTip> */}
                {logoUrl === undefined ? (
                  // Loading — show placeholder to prevent layout jump
                  <div
                    className={styles[`${cssClassName}__logo-placeholder`]}
                  />
                ) : logoUrl === null ? (
                  // Error — show default TT logo
                  <img
                    className={styles[`${cssClassName}__logo`]}
                    src={tomorrowThingsLogo}
                    alt={texts.ttLogoAltText}
                  />
                ) : (
                  // Success — show custom CDN logo
                  <img
                    className={styles[`${cssClassName}__logo`]}
                    src={logoUrl}
                    alt={texts.ttLogoAltText}
                  />
                )}
                <VerticalSeparator />
                <FlexColumnContainer verticalGap={4}>
                  <h1 className={styles[`${cssClassName}__title`]}>
                    <span>{texts.productName}</span>
                  </h1>
                  <p className={styles[`${cssClassName}__user-full-name`]}>
                    {`Welcome ${userFullName}`}
                  </p>
                </FlexColumnContainer>
              </div>

              <ul className={styles[`${cssClassName}__nav-list`]}>
                {/* {features.map((featureOrElement) => {
                  return (
                    <FeatureLink
                      key={featureOrElement.id}
                      featureOrElement={featureOrElement}
                    />
                  );
                })} */}

                {
                  // machines
                }
                <FeatureLink
                  key={features[2].id}
                  featureOrElement={features[2]}
                />
                {
                  // root path - sources list
                }
                <RenderIfUserHasPermission permission="manage-gateways">
                  <FeatureLink
                    key={features[0].id}
                    featureOrElement={features[0]}
                  />
                </RenderIfUserHasPermission>
                <RenderIfUserHasMultiplePermissions
                  permissions={["manage-all-tickets", "view-all-tickets"]}
                >
                  {isTicketingFeatureEnabled && (
                    <FeatureLink
                      key={features[3].id}
                      featureOrElement={features[3]}
                    />
                  )}
                </RenderIfUserHasMultiplePermissions>

                {showFactoryDashboard && (
                  <RenderIfUserHasMultiplePermissions
                    requireAll={true}
                    permissions={FACTORY_DASHBOARD_PERMISSIONS}
                  >
                    <FeatureLink
                      key={features[4].id}
                      featureOrElement={features[4]}
                    />
                  </RenderIfUserHasMultiplePermissions>
                )}
                <RenderIfUserHasPermission permission={"manage-dashboards"}>
                  <FeatureLink
                    key={features[5].id}
                    featureOrElement={features[5]}
                  />
                </RenderIfUserHasPermission>
                <RenderIfUserHasPermission permission={"view-dashboards"}>
                  <FeatureLink
                    key={features[6].id}
                    featureOrElement={features[6]}
                  />
                </RenderIfUserHasPermission>
                {isAdminPanelEnabled && (
                  <RenderIfUserHasPermission permission={"view-admin-panel"}>
                    <FeatureLink
                      key={features[7].id}
                      featureOrElement={features[7]}
                    />
                  </RenderIfUserHasPermission>
                )}
              </ul>
              <span
                className={styles[`${cssClassName}__help-logout-icons-wrapper`]}
              >
                <DropDownLanguage />
                <ChatIconButton
                  content={texts.chatAI}
                  ariaLabel={texts.chatAI}
                />
                <RenderIfUserHasPermission permission="create-tickets">
                  {isTicketingFeatureEnabled && (
                    <SupportTicketButton
                      content={texts.support}
                      ariaLabel={texts.support}
                    />
                  )}
                </RenderIfUserHasPermission>

                <RenderOnlyForDevelopers>
                  <NotificationsIconButton
                    content={texts.notifications}
                    ariaLabel={texts.notifications}
                  />
                </RenderOnlyForDevelopers>

                <RenderIfUserHasMultiplePermissions
                  permissions={[
                    "manage-user-self",
                    "manage-human-users",
                    "view-human-users",
                    "manage-service-api-users",
                    "view-service-api-users",
                    "view-service-gateway-users",
                    "manage-service-gateway-users",
                  ]}
                >
                  <UsersIconButton
                    content={texts.usersIconButton}
                    ariaLabel={texts.usersIconButton}
                    useHook={useUsersIconButtonHook}
                  />
                </RenderIfUserHasMultiplePermissions>
                <HelpIconWithPopover />
                <InfoIconWithPopover />
                <VerticalSeparator />
                <LogoutIconButtonWrapperWithTooltip
                  handleClick={() => handleLogOutClick()}
                />
              </span>
            </ThirdSplitContainer>
          </nav>
        </div>
      </DropAreaDeactivationOverlayContainer>
      {children}
    </>
  );
};
